import {
  makeStyles,
  Card,
  ActionButton,
  IconButton,
  Collapse,
  FormSectionTitle,
  Hidden,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  AddItemButton
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { StripeMark } from '@paintscout/ui/icons'
import { DialogStackContext, useClientOptions, useUser } from '@paintscout/ui-business'
import CheckIcon from '@material-ui/icons/Check'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import DownloadIcon from '@material-ui/icons/GetApp'
import LaunchIcon from '@material-ui/icons/Launch'
import CancelIcon from '@material-ui/icons/Cancel'
import UndoIcon from '@material-ui/icons/Undo'
import SendIcon from '@material-ui/icons/Send'
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight'
// import ReceiptIcon from '@material-ui/icons/Receipt'
// import { useApolloClient } from '@apollo/react-hooks'
// import { gql } from 'apollo-boost'
import {
  getPayments,
  getPaymentStage,
  canCancelProcessingPayment,
  canRefundPayment,
  requestHasProcessingPayment
} from '@paintscout/util/builder'
import { getPaymentPortalLink } from '@paintscout/util/util'
import classnames from 'classnames'

import moment from 'moment'
import type { PayrixPaymentType, QuoteDocument } from 'paintscout'
import React, { useContext } from 'react'
import { QuoteContext } from '../../../../context/QuoteContext'
import { QuotePdfDialog, SendPaymentRequestDialog, TileListDialog } from '../../../../dialogs'

import PaymentStatusBadgeWithTooltip from './PaymentStatusBadgeWithTooltip'
import { RECORD_PAYMENT_KEY, usePaymentActions } from './usePaymentActions'

const useStyles = makeStyles<Theme, PaymentsTableProps>(
  (theme) => ({
    root: {
      '& thead': {
        background: theme.paintscout.palette.brand.Neutral.P94
      },
      '& thead th, & thead th span': {
        color: theme.paintscout.palette.brand.Neutral.P35
      },
      '& thead th': {
        padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`
      },
      '& tbody td': {
        padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`
      }
    },
    readOnly: {
      '& tr': {
        cursor: 'default'
      }
    },
    mobileTitleValue: {
      fontWeight: theme.typography.fontWeightMedium
    },
    mobileIcons: {
      fontSize: 'inherit',
      verticalAlign: 'middle',
      marginRight: theme.typography.pxToRem(4)
    },
    mobileTitleIcon: {
      fontSize: '0.7rem'
    },
    mobileValueContainer: {
      ...theme.typography.body2,
      color: theme.paintscout.palette.brand.Neutral.P70,
      width: '100%'
    },
    mobileValue: {
      marginLeft: theme.typography.pxToRem(10)
    },
    mobileEstimatorContainer: {
      ...theme.typography.body2,
      color: theme.paintscout.palette.brand.Neutral.P70,
      width: '100%'
    },
    fixedCell: {
      width: 100
    },
    smallFixedCell: {
      width: 20
    },
    typeCell: {
      width: 120
    },
    dateCell: {
      width: 160
    },
    paymentRow: {
      '@media (hover: hover)': {
        '&:hover': {
          backgroundColor: theme.paintscout.palette.brand.Neutral.P98
        }
      }
    },
    noteCell: {
      maxWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    disabledActionButton: {
      color: 'transparent !important'
    },
    downloadButton: {
      color: theme.palette.text.secondary,
      padding: 0,
      background: 'none',
      boxShadow: 'none',
      '&:hover': {
        background: 'none',
        boxShadow: 'none'
      }
    },
    underline: {
      width: 'calc(100% - 2px)',
      overflow: 'hidden'
    },
    card: {
      borderRadius: theme.borderRadius.md,
      boxShadow: 'none',
      border: 'none'
    },
    marginTop: {
      marginTop: '2px'
    },
    nonSummedAmount: {
      '&$nonSummedAmount': {
        color: theme.palette.grey[500]
      }
    },
    refundArrow: {
      color: theme.palette.grey[500]
    },
    statusCell: {
      width: 110
    }
  }),
  { name: 'PaymentsTable' }
)

// const QUOTE_URL_QUERY = gql`
//   query quoteUrlForReceipt($id: String!, $rev: String!, $view: String!, $paymentId: String) {
//     quoteUrl(id: $id, rev: $rev, view: $view, paymentId: $paymentId) {
//       url {
//         link
//       }
//     }
//   }
// `

export interface PaymentsTableProps {
  classes?: StyleClasses<typeof useStyles>
  inPresentation?: boolean
  collapsed?: boolean
  onChange: (quote: QuoteDocument) => void
  onToggle?: (ev: any) => void
}

function PaymentsTable(props: PaymentsTableProps) {
  const { collapsed, inPresentation, onToggle, onChange } = props
  const classes = useStyles(props)
  const { quote, isEditable, save: saveQuote, isDirty } = useContext(QuoteContext)
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const { options, clientId } = useClientOptions()
  const { user, preferences, isPainter, canReversePayments } = useUser()

  // The Add/Request-Payment flow + the shared edit/configure/collect/delete handlers + eligibility
  // now live in usePaymentActions, so the estimateNext header's "+ Add → Payment" opens the exact
  // same dialogs. This table keeps the row-only actions (refund/resend/download/view-in-portal) and
  // rendering.
  const {
    payments,
    displayPayments,
    achLabel,
    isPaymentEligible,
    canCollectCCPayment,
    canCollectACHPayment,
    deletePayment,
    refundPayment,
    cancelProcessingPayment,
    handleEditPayment,
    handleCollectPayment,
    handleAddRequestPayment,
    checkingForNewPayments
  } = usePaymentActions({ inPresentation, onChange })

  if (collapsed && !isEditable) {
    return null
  }

  async function handleDownloadPDF(index: number) {
    const payment = payments[index]
    if (isDirty) {
      await saveQuote()
    }
    openDialog(QuotePdfDialog, {
      quote,
      companyId: clientId,
      options,
      silent: true,
      view: 'payment-receipt',
      paymentId: payment.id,
      onConfirm: dismissDialog
    })
  }

  // keeping this code around for a bit for testing but not meant to be a real feature for users
  // also need to remove the & pdf from apps/customer-view/src/pages/Index.tsx to make this work

  // async function handleViewReceipt(paymentId: string) {
  //   if (isDirty) {
  //     await saveQuote()
  //   }
  //   const result = await apolloClient.query({
  //     query: QUOTE_URL_QUERY,
  //     variables: { id: quote._id, rev: quote._rev, view: 'payment-receipt', paymentId },
  //     fetchPolicy: 'network-only'
  //   })
  //   const link = result?.data?.quoteUrl?.url?.link
  //   if (link) {
  //     window.open(link, '_blank', 'noopener,noreferrer')
  //   }
  // }

  async function handleActionClick(actionKey: string, index: number) {
    const payments = getPayments({ quote })
    const payment = payments[index]
    switch (actionKey) {
      case 'delete':
        deletePayment(index)
        break
      case 'edit':
        handleEditPayment({ paymentIndex: index })
        break
      case 'refund':
        refundPayment(payment)
        break
      case 'cancel-processing':
        cancelProcessingPayment(payment)
        break
      case 'download':
        handleDownloadPDF(index)
        break
      // case 'view-receipt':
      //   handleViewReceipt(payment.id)
      //   break
      case 'resend':
        openDialog(SendPaymentRequestDialog, {
          payment: payments[index],
          user,
          preferences,
          isResend: true
        })
        break
      case 'mark-as-paid':
        openDialog(TileListDialog, {
          title: 'Mark as Successful',
          showTopClose: true,
          items: [
            {
              key: RECORD_PAYMENT_KEY,
              label: 'Record Payment'
            },
            canCollectCCPayment && {
              key: 'credit',
              label: 'Take Credit Card Payment'
            },
            canCollectACHPayment && {
              key: 'ach',
              label: `Take ${achLabel} Payment`
            }
          ].filter(Boolean),
          onItemSelect: (key: string) => {
            if (key === RECORD_PAYMENT_KEY) {
              handleEditPayment({ paymentIndex: index, backOnCancel: true, markingPaid: true })
            } else {
              handleCollectPayment({
                amount: payment.amount,
                note: payment.note,
                deposit: payment.deposit,
                paymentRequestId: payment.id,
                paymentType: key as PayrixPaymentType
              })
            }
          }
        })
        break
      case 'view-in-portal': {
        const portalLink = getPaymentPortalLink(payment.providerDetails, options)
        if (portalLink) {
          window.open(portalLink, '_blank', 'noopener,noreferrer')
        }
        break
      }
    }
  }

  const getActions = (index: number) => {
    const payment = payments[index]
    const isRequestWithProcessingPayment = requestHasProcessingPayment({ quote, payment })
    const showResend = !!payments[index] && payment.status === 'pending' && !(payment.amount > quote.totals.balance_due)
    const isProcessedPayment = !!payment?.activityId && !payment?.manual

    return [
      !isRequestWithProcessingPayment &&
        showResend && {
          key: 'resend',
          icon: SendIcon,
          label: 'Resend'
        },
      !isRequestWithProcessingPayment &&
        payment.status === 'pending' && {
          key: 'edit',
          icon: EditIcon,
          label: 'Edit'
        },
      !isRequestWithProcessingPayment &&
        payment.status === 'pending' && {
          key: 'mark-as-paid',
          icon: CheckIcon,
          label: 'Mark as Successful'
        },
      // Painters can't refund or cancel payments, even with payment perms (ENG-870, see
      // canReversePayments). The inline ViewPaymentDialog buttons use the same gate in
      // usePaymentActions, and the API enforces it.
      canReversePayments &&
        canRefundPayment(payment, payments) && {
          key: 'refund',
          icon: UndoIcon,
          label: 'Refund'
        },
      // The API re-checks the Payrix status, so a settled txn gets a "contact support" dialog
      // rather than a cancel.
      canReversePayments &&
        canCancelProcessingPayment(payment) && {
          key: 'cancel-processing',
          icon: CancelIcon,
          label: 'Cancel Payment'
        },
      // Receipt download hidden for painters — see onReceipt gate above (ENG-846).
      !isPainter &&
        ['paid', 'refunded', 'failed'].includes(payment.status) && {
          key: 'download',
          icon: DownloadIcon,
          label: 'Download Receipt'
        },
      // ['paid', 'refunded', 'failed'].includes(payment.status) && {
      //   key: 'view-receipt',
      //   icon: ReceiptIcon,
      //   label: 'View Receipt'
      // },
      isProcessedPayment &&
        (payment.providerDetails?.payrix?.txnId ?? payment.providerDetails?.stripe?.paymentIntentId) && {
          key: 'view-in-portal',
          icon: payment.providerDetails?.stripe?.paymentIntentId ? StripeMark : LaunchIcon,
          label: payment.providerDetails?.stripe?.paymentIntentId ? 'View in Stripe' : 'View in Portal'
        },
      !isRequestWithProcessingPayment &&
        !isProcessedPayment && {
          key: 'delete',
          icon: DeleteIcon,
          label: 'Delete'
        }
    ].filter(Boolean)
  }

  return (
    <div className={`${!isEditable && !inPresentation ? classes.readOnly : ''} ${classes.root}`} id="payments">
      <FormSectionTitle title={'Payments'} showToggle={isEditable} toggleValue={!collapsed} onToggle={onToggle} />
      <Collapse show={!collapsed}>
        <Card className={classes.card}>
          <Table component="table">
            <TableHead component="thead">
              <TableRow component="tr">
                {/* desktop header */}
                <Hidden smDown implementation="js">
                  <TableCell component="th" className={classes.fixedCell}>
                    Amount
                  </TableCell>
                  <TableCell component="th" className={classes.statusCell}>
                    Status
                  </TableCell>
                  <TableCell component="th" className={classes.fixedCell}>
                    Method
                  </TableCell>
                  <TableCell component="th">{inPresentation ? 'Note' : 'Client Note'}</TableCell>
                  <TableCell component="th" className={classes.dateCell} align="right">
                    Date
                  </TableCell>
                  {/* action column or download receipt column - depending on inPresentation */}
                  {<TableCell component="th" className={classes.smallFixedCell} />}
                </Hidden>

                {/* mobile header */}
                <Hidden mdUp implementation="js">
                  <TableCell component="th">Payment</TableCell>
                </Hidden>
              </TableRow>
            </TableHead>
            <TableBody component="tbody">
              {displayPayments.map((payment, displayIndex) => {
                const originalIndex = payments.findIndex((p) => p.id === payment.id)
                const isChildRow =
                  !!payment.forPaymentId ||
                  (!inPresentation &&
                    !!payment.paymentRequestId &&
                    (payment.status === 'processing' || payment.status === 'failed'))

                // refunds are stored as negative amounts; show them as positive since the
                // "Refunded" badge already conveys direction (and a leading "-" can wrap awkwardly)
                const isRefund = !!payment.forPaymentId || payment.status === 'refunded'
                const displayAmount = isRefund ? Math.abs(payment.amount) : payment.amount

                const paymentStage = getPaymentStage({ payment, quote })

                const actions = getActions(originalIndex)

                return (
                  <TableRow
                    component="tr"
                    className={isEditable || inPresentation ? classes.paymentRow : ''}
                    key={payment.id ?? displayIndex}
                    noBorder={displayIndex === displayPayments.length - 1}
                    onClick={() => handleEditPayment({ paymentIndex: originalIndex })}
                  >
                    {/* desktop  */}
                    <Hidden smDown implementation="js">
                      <TableCell component="td">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {isChildRow && (
                            <SubdirectoryArrowRightIcon fontSize="small" className={classes.refundArrow} />
                          )}
                          <div>
                            <Typography
                              className={classnames({
                                [classes.nonSummedAmount]: !['paid', 'refunded'].includes(payment.status)
                              })}
                              style={{ fontWeight: 500 }}
                              value={displayAmount}
                              format={'price'}
                              showUnits={true}
                              variant={'body1'}
                            />
                            {paymentStage && <Typography variant={'subtitle2'}>{paymentStage}</Typography>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell component="td" className={classes.statusCell}>
                        <PaymentStatusBadgeWithTooltip
                          status={payment.status}
                          amount={payment.amount}
                          type={payment.type}
                          balanceDue={quote.totals.balance_due}
                          achLabel={achLabel}
                          failedEvents={payment.failedEvents}
                          failureReason={payment.failureReason}
                          inPresentation={inPresentation}
                        />
                      </TableCell>
                      <TableCell component="td" className={classes.typeCell}>
                        {payment.type}
                      </TableCell>
                      <TableCell component="td" className={classes.noteCell}>
                        {payment.note}
                      </TableCell>
                      <TableCell component="td" className={classes.dateCell} align="right">
                        <Tooltip hideIcon content={moment(payment.date).format('MMMM D, YYYY, h:mm a')}>
                          <span>{moment(payment.date).format('MMM D, YYYY')}</span>
                        </Tooltip>
                      </TableCell>
                      {inPresentation ? (
                        <TableCell component="td" align="right">
                          {['paid', 'refunded', 'failed'].includes(payment.status) && (
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDownloadPDF(originalIndex)
                              }}
                              size="small"
                            >
                              <DownloadIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      ) : (
                        <TableCell isControl={true} component="td" align="right">
                          <ActionButton
                            className="allow-propagation"
                            actions={actions}
                            onActionClick={(ev, actionKey) => {
                              handleActionClick(actionKey, originalIndex)
                            }}
                            disabled={actions.length === 0}
                          />
                        </TableCell>
                      )}
                    </Hidden>

                    {/* mobile column */}
                    <Hidden mdUp implementation="js">
                      <TableCell component="td">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                            {isChildRow && (
                              <SubdirectoryArrowRightIcon fontSize="small" className={classes.refundArrow} />
                            )}
                            <div>
                              <Typography
                                className={classnames({
                                  [classes.nonSummedAmount]: !['paid', 'refunded'].includes(payment.status)
                                })}
                                style={{ fontWeight: 500 }}
                                value={displayAmount}
                                format={'price'}
                                showUnits={true}
                                variant={'body1'}
                              />
                              <div className={classes.mobileValueContainer}>
                                {paymentStage && `${paymentStage} • `}
                                {moment(payment.date).format('MMM D, YYYY')}
                              </div>
                            </div>
                          </div>
                          <PaymentStatusBadgeWithTooltip
                            status={payment.status}
                            amount={payment.amount}
                            type={payment.type}
                            balanceDue={quote.totals.balance_due}
                            achLabel={achLabel}
                          />
                        </div>
                      </TableCell>
                    </Hidden>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          {!inPresentation && (
            <AddItemButton
              id="allow-propagation"
              className={!payments.length ? classes.marginTop : ''}
              loading={checkingForNewPayments}
              onClick={handleAddRequestPayment}
            >{`${isPaymentEligible ? 'Add/Request' : 'Add'} Payment`}</AddItemButton>
          )}
        </Card>
      </Collapse>
    </div>
  )
}

export default PaymentsTable
